import React, { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'
import {Search, Filter, Download, TrendingUp, TrendingDown, Calendar, FileText, Database} from 'lucide-react'

interface PointsTransaction {
  _id: string
  customerId: string
  customerEmail: string
  customerName?: string
  type: 'earned' | 'used' | 'refund' | 'adjustment'
  points: number
  balanceBefore: number
  balanceAfter: number
  orderId?: string
  orderNumber?: string
  amount?: number
  description?: string
  createdAt: string
  createdBy?: string
}

const PointsTransactionsManager: React.FC = () => {
  const [transactions, setTransactions] = useState<PointsTransaction[]>([])
  const [filteredTransactions, setFilteredTransactions] = useState<PointsTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [searchEmail, setSearchEmail] = useState('')
  const [tempSearchEmail, setTempSearchEmail] = useState('') // 临时搜索输入
  const [filterType, setFilterType] = useState<string>('all')
  const [tempFilterType, setTempFilterType] = useState<string>('all') // 临时类型筛选
  const [startDate, setStartDate] = useState('')
  const [tempStartDate, setTempStartDate] = useState('') // 临时开始日期
  const [endDate, setEndDate] = useState('')
  const [tempEndDate, setTempEndDate] = useState('') // 临时结束日期
  const [isMigrating, setIsMigrating] = useState(false)

  // 统计数据
  const [stats, setStats] = useState({
    totalEarned: 0,
    totalUsed: 0,
    totalTransactions: 0
  })

  // 加载交易记录
  const loadTransactions = async () => {
    setLoading(true)
    try {
      const response = await lumi.entities.points_transactions.list({
        sort: { createdAt: -1 },
        limit: 1000
      })
      const data = response?.list || []
      setTransactions(data)
      setFilteredTransactions(data)
      calculateStats(data)
    } catch (error) {
      console.error('加载点数交易记录失败:', error)
      alert('加载失败，请稍后再试')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTransactions()
  }, [])

  // 计算统计数据
  const calculateStats = (data: PointsTransaction[]) => {
    const totalEarned = data
      .filter(t => t.type === 'earned' || t.type === 'refund')
      .reduce((sum, t) => sum + Math.abs(t.points), 0)
    
    const totalUsed = data
      .filter(t => t.type === 'used')
      .reduce((sum, t) => sum + Math.abs(t.points), 0)

    setStats({
      totalEarned,
      totalUsed,
      totalTransactions: data.length
    })
  }

  // 筛选逻辑
  useEffect(() => {
    let filtered = [...transactions]

    // 邮箱搜索
    if (searchEmail) {
      filtered = filtered.filter(t => 
        t.customerEmail?.toLowerCase().includes(searchEmail.toLowerCase()) ||
        t.customerName?.toLowerCase().includes(searchEmail.toLowerCase())
      )
    }

    // 类型筛选
    if (filterType !== 'all') {
      filtered = filtered.filter(t => t.type === filterType)
    }

    // 日期范围筛选
    if (startDate) {
      filtered = filtered.filter(t => t.createdAt >= startDate)
    }
    if (endDate) {
      const endDateTime = new Date(endDate)
      endDateTime.setHours(23, 59, 59, 999)
      filtered = filtered.filter(t => new Date(t.createdAt) <= endDateTime)
    }

    setFilteredTransactions(filtered)
    calculateStats(filtered)
  }, [searchEmail, filterType, startDate, endDate, transactions])

  // 交易类型标签样式
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'earned':
        return <span className="px-2 py-1 bg-green-900 text-green-300 text-xs">获得</span>
      case 'used':
        return <span className="px-2 py-1 bg-red-900 text-red-300 text-xs">使用</span>
      case 'refund':
        return <span className="px-2 py-1 bg-blue-900 text-blue-300 text-xs">退款</span>
      case 'adjustment':
        return <span className="px-2 py-1 bg-yellow-900 text-yellow-300 text-xs">调整</span>
      default:
        return <span className="px-2 py-1 bg-gray-800 text-gray-300 text-xs">{type}</span>
    }
  }

  // 手动执行搜索（统一更新所有筛选条件）
  const handleSearch = async () => {
    setSearchEmail(tempSearchEmail)
    setFilterType(tempFilterType)
    setStartDate(tempStartDate)
    setEndDate(tempEndDate)
    
    // 重新从数据库加载最新数据
    await loadTransactions()
  }

  // 重置筛选
  const handleReset = () => {
    setTempSearchEmail('')
    setTempFilterType('all')
    setTempStartDate('')
    setTempEndDate('')
    setSearchEmail('')
    setFilterType('all')
    setStartDate('')
    setEndDate('')
  }

  // 导出 CSV
  const exportToCSV = () => {
    if (filteredTransactions.length === 0) {
      alert('没有数据可导出')
      return
    }

    const headers = ['时间', '会员邮箱', '会员姓名', '类型', '点数变动', '变动前余额', '变动后余额', '订单编号', '金额', '描述', '操作者']
    const rows = filteredTransactions.map(t => [
      new Date(t.createdAt).toLocaleString('zh-TW'),
      t.customerEmail,
      t.customerName || '-',
      t.type,
      t.points,
      t.balanceBefore,
      t.balanceAfter,
      t.orderNumber || '-',
      t.amount ? `NT$ ${t.amount}` : '-',
      t.description || '-',
      t.createdBy || 'system'
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `points_transactions_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  // 执行数据迁移
  const handleMigrate = async () => {
    if (!confirm('确认执行历史数据迁移？\n\n这将从历史订单创建点数交易记录。')) {
      return
    }

    setIsMigrating(true)
    try {
      const response = await lumi.functions.invoke('migratePointsTransactions', {})
      
      if (response.success) {
        alert(`迁移成功！\n\n创建 ${response.report?.summary?.createdTransactions || 0} 笔记录\n跳过 ${response.report?.summary?.skippedTransactions || 0} 笔重复记录\n影响 ${response.report?.summary?.affectedCustomers || 0} 位会员`)
        await loadTransactions()
      } else {
        throw new Error(response.error || '迁移失败')
      }
    } catch (error) {
      console.error('迁移失败:', error)
      alert(`迁移失败：${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setIsMigrating(false)
    }
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center text-gray-400">加载中...</div>
      </div>
    )
  }

  return (
    <div className="p-8 bg-gray-900 min-h-screen">
      {/* 页面标题 */}
      <div className="mb-8">
        <h2 className="text-3xl font-light tracking-wider mb-2 text-gray-100">点数交易记录</h2>
        <p className="text-gray-400">查看所有会员的点数变动历史</p>
        
        {/* 迁移按钮（仅在无数据时显示） */}
        {transactions.length === 0 && !loading && (
          <div className="mt-4 p-4 bg-blue-900/20 border border-blue-700">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Database size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-blue-300 mb-2">尚未迁移历史数据</h3>
                <p className="text-sm text-gray-400 mb-4">
                  检测到点数交易记录表为空。您可以执行一键迁移，系统将自动从历史订单生成完整的点数交易记录。
                </p>
                <button
                  onClick={handleMigrate}
                  disabled={isMigrating}
                  className="px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <Database size={18} />
                  {isMigrating ? '正在迁移...' : '执行数据迁移'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-600 flex items-center justify-center">
              <TrendingUp size={24} className="text-white" />
            </div>
          </div>
          <div className="text-3xl font-light text-gray-100 mb-2">
            {stats.totalEarned.toLocaleString()}
          </div>
          <div className="text-sm text-gray-400">累计获得点数</div>
        </div>

        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-red-600 flex items-center justify-center">
              <TrendingDown size={24} className="text-white" />
            </div>
          </div>
          <div className="text-3xl font-light text-gray-100 mb-2">
            {stats.totalUsed.toLocaleString()}
          </div>
          <div className="text-sm text-gray-400">累计使用点数</div>
        </div>

        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-600 flex items-center justify-center">
              <FileText size={24} className="text-white" />
            </div>
          </div>
          <div className="text-3xl font-light text-gray-100 mb-2">
            {stats.totalTransactions.toLocaleString()}
          </div>
          <div className="text-sm text-gray-400">总交易笔数</div>
        </div>
      </div>

      {/* 筛选工具栏 */}
      <div className="bg-gray-950 border border-gray-800 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* 邮箱搜索 */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
            <input
              type="text"
              placeholder="搜索会员邮箱或姓名"
              value={tempSearchEmail}
              onChange={(e) => setTempSearchEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 text-gray-100 focus:border-blue-500 focus:outline-none"
            />
          </div>

          {/* 类型筛选 */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
            <select
              value={tempFilterType}
              onChange={(e) => setTempFilterType(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 text-gray-100 focus:border-blue-500 focus:outline-none appearance-none"
            >
              <option value="all">所有类型</option>
              <option value="earned">获得</option>
              <option value="used">使用</option>
              <option value="refund">退款</option>
              <option value="adjustment">调整</option>
            </select>
          </div>

          {/* 开始日期 */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
            <input
              type="date"
              value={tempStartDate}
              onChange={(e) => setTempStartDate(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 text-gray-100 focus:border-blue-500 focus:outline-none"
            />
          </div>

          {/* 结束日期 */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
            <input
              type="date"
              value={tempEndDate}
              onChange={(e) => setTempEndDate(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 text-gray-100 focus:border-blue-500 focus:outline-none"
            />
          </div>

          {/* 搜索按钮 */}
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <Search size={18} />
            搜索
          </button>

          {/* 导出按钮 */}
          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
          >
            <Download size={18} />
            导出 CSV
          </button>
        </div>

        {/* 筛选结果提示 */}
        {(searchEmail || filterType !== 'all' || startDate || endDate) && (
          <div className="mt-4 text-sm text-gray-400">
            显示 {filteredTransactions.length} 条记录（共 {transactions.length} 条）
          </div>
        )}
      </div>

      {/* 交易记录表格 */}
      <div className="bg-gray-950 border border-gray-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900 border-b border-gray-800">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">时间</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">会员</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">类型</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">点数变动</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">变动前</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">变动后</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">订单编号</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">金额</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">描述</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    暂无交易记录
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((transaction) => (
                  <tr key={transaction._id} className="hover:bg-gray-900 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {new Date(transaction.createdAt).toLocaleString('zh-TW', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      <div className="font-medium">{transaction.customerName || '-'}</div>
                      <div className="text-xs text-gray-500">{transaction.customerEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getTypeLabel(transaction.type)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <span className={transaction.points >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {transaction.points >= 0 ? '+' : ''}{transaction.points.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-400">
                      {transaction.balanceBefore.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-300 font-medium">
                      {transaction.balanceAfter.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {transaction.orderNumber || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-300">
                      {transaction.amount ? `NT$ ${transaction.amount.toLocaleString()}` : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400 max-w-xs truncate">
                      {transaction.description || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default PointsTransactionsManager
